import { FC, useRef, useEffect } from 'react';
import { useMutation } from 'react-query';
import { observer } from 'mobx-react-lite';
import { FormProvider, useForm } from 'react-hook-form';
import { useParams } from 'react-router';
import { useStore } from 'hooks/useRootStore';
import { useMyTranslations } from 'hooks/useMyTranslations';
import { updateImageUrl } from 'utils/convertString';
import { Button } from 'components/Core/Button';
import AutocompleteSelect from 'components/Inputs/AutocompleteSelect';
import { CatalogueProductsTable } from 'components/Tables/Brand/CatalogueProductsTable/CatalogueProductsTable';
import { CatalogueProductLayer } from '../CatalogueProductLayer/CatalogueProductLayer';
import { IProductData } from 'stores/products/types';
import { BrandCataloguesPageFields, IBrandCatalogueForm, IBrandCataloguesPageProps } from './types';
import { DEFAULT_PRODUCT_POSITION, IMAGE_HEIGHT, IMAGE_WIDTH, MAX_MENU_HEIGHT } from './constants';
import { brandCataloguesTranslations } from './translations';
import styles from './brandCataloguesPage.module.scss';

export const BrandCataloguesPage: FC<IBrandCataloguesPageProps> = observer(({ page }) => {
  const {
    brandsStore: { brandsCatalogue, editBrandCatalogue },
    productsStore: { getAutoCompleteProducts },
    popUpStore: { openPopUp },
    alertStore: { showAlert },
  } = useStore();

  const { t } = useMyTranslations<BrandCataloguesPageFields>([brandCataloguesTranslations.name]);

  const { mutate, isLoading } = useMutation(editBrandCatalogue, {
    mutationKey: 'editBrandCatalogue',
    onSuccess: () => {
      showAlert(t('editCatalogueSuccess'), 'success');
    },
  });

  const { pageNumber } = useParams();

  const containerRef = useRef<HTMLDivElement | null>(null);

  const useFormMethods = useForm<IBrandCatalogueForm>({ defaultValues: { products: page.products } });
  const {
    handleSubmit,
    setValue,
    getValues,
    watch,
    formState: { isSubmitting },
  } = useFormMethods;

  useEffect(() => {
    if (!watch('productSearch')) return;
    const product = watch('productSearch');
    setValue('products', getValues('products').concat({ ...product, position: DEFAULT_PRODUCT_POSITION }));
  }, [watch('productSearch')]);

  const onSubmitHandler = (data: IBrandCatalogueForm) => {
    if (brandsCatalogue && pageNumber) {
      const newCatalogue = { ...brandsCatalogue };
      newCatalogue.pages[+pageNumber - 1].products = data.products;
      mutate(newCatalogue);
    }
  };

  return (
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    <FormProvider {...(useFormMethods as any)}>
      <div className={styles.pageView}>
        <div className={styles.imageBox}>
          <div className={styles.imageWrapper} ref={containerRef} style={{ width: IMAGE_WIDTH, height: IMAGE_HEIGHT }}>
            {watch('products')?.map((item) => (
              <CatalogueProductLayer
                key={item.id}
                product={item}
                imageBoxSize={{
                  width: containerRef.current?.clientWidth ?? IMAGE_WIDTH,
                  height: containerRef.current?.clientHeight ?? IMAGE_HEIGHT,
                }}
              />
            ))}
            <img
              className={styles.image}
              src={updateImageUrl(page.imageUrl, IMAGE_WIDTH * 2, IMAGE_HEIGHT * 2)}
              loading='lazy'
            />
          </div>
        </div>
        <div className={styles.leftSide}>
          <div className={styles.cataloguePageTable}>
            <div className={styles.headerRow}>
              <div className={styles.searchInput}>
                <AutocompleteSelect
                  name='productSearch'
                  placeholder={t(`select`)}
                  data={[]}
                  onSearch={getAutoCompleteProducts}
                  styleProps={{ controlStyles: { background: 'white' }, containerStyles: { marginBottom: 0 } }}
                  // disableBase64Converting
                  filterArray={watch('products')}
                  isClearable={false}
                  // showSelected={false}
                  // entityType='products'
                  maxMenuHeight={MAX_MENU_HEIGHT}
                />
              </div>
              <Button
                title={'Add new product'}
                classNameProp={`${styles.searchBtn}`}
                onClick={() =>
                  openPopUp({
                    formName: 'addProduct',
                    cb: (product: IProductData) =>
                      setValue(
                        'products',
                        getValues('products').concat({ ...product, position: DEFAULT_PRODUCT_POSITION })
                      ),
                  })
                }
                isDisabled={isSubmitting || isLoading}
              />
            </div>

            <CatalogueProductsTable />
          </div>
          <Button title={t('Save')} onClick={handleSubmit(onSubmitHandler)} isDisabled={isSubmitting || isLoading} />
        </div>
      </div>
    </FormProvider>
  );
});
